/* OperationalEventSheet.jsx — the "What happened?" interaction surface.
 *
 * Replaces stage-ID editing with operational events: agents pick a
 * real-world milestone ("Mortgage offer received") and the backend
 * derives the stage advance + writes a human-worded timeline row.
 *
 * Doctrine:
 *   • Calm. Operational. Real-transaction language.
 *   • Tap → instant optimistic toast + sheet close. Background PATCH
 *     reconciles. On failure, revert and surface calm error toast.
 *   • Events are grouped by milestone (Instruction → Completion).
 *   • Leasehold-only events are hidden on freehold deals.
 *   • Single source of truth — every tap writes to deal_activities.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, Check, ChevronRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SlideOver } from "./DealRoomSheets";
import { api } from "../../lib/api";
import { invalidateDealActivity, useDealActivity } from "../../hooks/useDealActivity";
import { useChainStore } from "../../data/chainStore";

/* Lazy-loaded catalogue cache — small, immutable, fetched once per session. */
let _CATALOGUE = null;
let _CATALOGUE_PROMISE = null;

async function loadCatalogue() {
  if (_CATALOGUE) return _CATALOGUE;
  if (_CATALOGUE_PROMISE) return _CATALOGUE_PROMISE;
  _CATALOGUE_PROMISE = api.get("/api/deals/operational-events/catalogue")
    .then(({ data }) => {
      _CATALOGUE = data?.groups || [];
      return _CATALOGUE;
    })
    .catch(() => {
      _CATALOGUE_PROMISE = null;
      return [];
    });
  return _CATALOGUE_PROMISE;
}

const GROUP_DOT = {
  instruction:   "#94A3B8",
  under_offer:   "#8B5CF6",
  pre_exchange:  "#3B82F6",
  exchange:      "#10B981",
  completion:    "#059669",
};

export function OperationalEventSheet({
  open, onClose, dealId, deal, onLogged, surface,
}) {
  const { logOperationalEvent } = useChainStore();
  const [groups, setGroups]   = useState([]);
  const [busy, setBusy]       = useState(null); // event_key currently posting
  const [recentKeys, setRecent] = useState(new Set());

  /* Derive completion state from the deal's activity timeline.
   * The activity log is the single source of truth — every operational
   * event tap writes a row with `meta.event_key`, so a deterministic
   * Set(eventKey) tells us what's already happened. */
  const activities = useDealActivity(dealId, { limit: 100, refreshKey: open ? 1 : 0 });
  const completedKeys = useMemo(() => {
    const s = new Set(recentKeys);
    for (const a of (activities || [])) {
      const k = a?.meta?.event_key || a?.event_key;
      if (k) s.add(k);
    }
    return s;
  }, [activities, recentKeys]);

  const isLeasehold = useMemo(() => {
    const t = String(deal?.tenure || "").toLowerCase();
    return t.includes("lease");
  }, [deal]);

  useEffect(() => {
    if (!open) return;
    loadCatalogue().then(setGroups);
    setRecent(new Set());
  }, [open]);

  const visibleGroups = useMemo(() => {
    return groups.map((g) => ({
      ...g,
      events: g.events.filter((e) => isLeasehold || !e.leasehold_only),
    })).filter((g) => g.events.length > 0);
  }, [groups, isLeasehold]);

  /* Recommended = the first event in catalogue order that hasn't been
   * completed yet. Exactly one item carries the recommended highlight
   * at any moment — calm, operational, never competes with itself. */
  const recommendedKey = useMemo(() => {
    for (const g of visibleGroups) {
      for (const e of g.events) {
        if (!completedKeys.has(e.key)) return e.key;
      }
    }
    return null;
  }, [visibleGroups, completedKeys]);

  const handleEvent = useCallback(async (ev) => {
    if (busy) return;
    setBusy(ev.key);
    try {
      const data = await logOperationalEvent(dealId, ev.key, {
        surface: surface || "what-happened-sheet",
        optimisticAdvanceTo: ev.advances_to || undefined,
      });
      toast.success(ev.timeline_title, {
        description: data?.stage_advanced
          ? `Stage advanced to ${prettyStage(data.to_stage)}.`
          : "Logged to the deal timeline.",
      });
      setRecent((s) => new Set(s).add(ev.key));
      invalidateDealActivity(dealId);
      onLogged?.(data);
      // Close after a brief beat so the operator sees the green tick.
      setTimeout(() => onClose?.(), 280);
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "deal_not_found") {
        toast.error("This deal is no longer in your workspace", {
          description: "Workspace refreshed. Please try again.",
        });
      } else {
        const detail = err?.response?.data?.detail;
        const description =
          (detail && typeof detail === "object" && detail.message) ||
          (typeof detail === "string" ? detail : null) ||
          (err?.message || "Please try again.");
        toast.error("Could not log this event", { description });
      }
    } finally {
      setBusy(null);
    }
  }, [busy, dealId, logOperationalEvent, surface, onLogged, onClose]);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="What happened?"
      subtitle="Log a real transaction event. The stage updates itself."
      testid="operational-event-sheet"
    >
      <div className="space-y-5" data-testid="op-event-groups">
        {visibleGroups.length === 0 ? (
          <p className="text-[12.5px] text-slate-400 italic" data-testid="op-event-empty">
            Loading operational events…
          </p>
        ) : (
          visibleGroups.map((g) => (
            <section key={g.id} data-testid={`op-event-group-${g.id}`}>
              <header className="flex items-center gap-2 mb-2">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: GROUP_DOT[g.id] || "#94A3B8" }}
                />
                <h3 className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  {g.label}
                </h3>
              </header>
              <ul className="grid grid-cols-1 gap-1.5">
                {g.events.map((e) => {
                  const status =
                    completedKeys.has(e.key)
                      ? "completed"
                      : recommendedKey === e.key
                        ? "recommended"
                        : "pending";
                  const isBusy = busy === e.key;
                  return (
                    <li key={e.key}>
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => handleEvent(e)}
                        data-testid={`op-event-${e.key}`}
                        data-status={status}
                        className={`w-full h-11 px-3.5 rounded-xl border bg-white text-left flex items-center gap-3 transition-all ${
                          isBusy
                            ? "border-violet-300 bg-violet-50/60"
                            : status === "recommended"
                              ? "border-violet-300 bg-white"
                              : status === "completed"
                                ? "border-emerald-100/80 bg-emerald-50/30 hover:border-emerald-200"
                                : "border-slate-100 hover:border-violet-200 hover:bg-violet-50/40"
                        } ${busy && !isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
                        style={{
                          boxShadow:
                            status === "recommended"
                              ? "0 0 0 1px rgba(167,139,250,0.18), 0 6px 18px -10px rgba(124,58,237,0.32)"
                              : "0 1px 2px rgba(15,23,42,0.03)",
                        }}
                      >
                        {/* Left-side status indicator */}
                        <span className="shrink-0 inline-flex items-center justify-center">
                          {isBusy ? (
                            <Loader2 size={18} className="text-violet-600 animate-spin" />
                          ) : status === "completed" ? (
                            <CheckCircle2 size={20} className="text-emerald-500" strokeWidth={1.8} aria-label="Completed" />
                          ) : status === "recommended" ? (
                            <span
                              aria-label="Recommended next step"
                              className="block w-[18px] h-[18px] rounded-full border-2 border-violet-400 bg-white"
                              style={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,1), 0 0 0 3px rgba(167,139,250,0.10)" }}
                            />
                          ) : (
                            <span
                              aria-label="Pending"
                              className="block w-[18px] h-[18px] rounded-full border border-slate-300 bg-white"
                            />
                          )}
                        </span>
                        <span className={`flex-1 truncate text-[13px] ${
                          status === "completed" ? "font-semibold text-slate-900"
                            : status === "recommended" ? "font-semibold text-slate-900"
                            : "font-medium text-slate-700"
                        }`}>
                          {e.label}
                        </span>
                        <ChevronRight size={13} className="text-slate-300 shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
        <p
          className="text-[11px] text-slate-400 pt-2 border-t border-slate-100"
          data-testid="op-event-fallback-hint"
        >
          Need a manual override? Use <span className="font-semibold text-slate-500">Update stage</span> instead.
        </p>
      </div>
    </SlideOver>
  );
}

const STAGE_LABEL = {
  offer: "Offer accepted",
  aml: "AML / ID",
  memo: "Memorandum issued",
  solicitors: "Solicitors instructed",
  mortgage_app: "Mortgage application",
  survey: "Survey booked",
  searches: "Searches",
  enquiries: "Enquiries",
  mortgage_offer: "Mortgage offer",
  exchange_ready: "Ready to exchange",
  exchanged: "Exchanged",
  completed: "Completed",
};
export function prettyStage(s) {
  return STAGE_LABEL[s] || s;
}

export default OperationalEventSheet;
